import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  FaExclamationTriangle,
  FaMousePointer,
  FaBan,
  FaHourglassHalf,
  FaRedoAlt,
  FaSearch,
  FaFilter,
  FaInfoCircle
} from 'react-icons/fa';
import { getFrictionSignals, getFrictionEvidence } from '../../../../services/customerAnalytics.service';
import { toExclusiveToDate } from '../../../../services/heatmap.service';
import FrictionEvidenceDrawer from '../drawers/FrictionEvidenceDrawer';
import './FrictionSignalsTab.css';

/**
 * Tab: Friction Signals Dashboard & Evidence Inspector.
 * Displays backend-derived rage clicks, dead clicks, interaction stalls, and repeated validation failures.
 * (APIs #18 & #19)
 */
export default function FrictionSignalsTab({
  organizationId,
  siteCollectionId,
  fromDate,
  toDate,
  pageId = '',
  pages = [],
  hasSessionReplayFeature = false,
  onWatchReplay,
  refreshNonce = 0,
  onLoadingChange
}) {
  const [signals, setSignals] = useState([]);
  const [apiResult, setApiResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedPageFilter, setSelectedPageFilter] = useState(pageId || 'all');

  useEffect(() => {
    setSelectedPageFilter(pageId || 'all');
  }, [pageId]);

  // Evidence Drawer state
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [evidenceData, setEvidenceData] = useState(null);
  const [isEvidenceLoading, setIsEvidenceLoading] = useState(false);
  const [evidenceError, setEvidenceError] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Load signals
  const fetchSignals = useCallback(async () => {
    if (!organizationId || !siteCollectionId || !fromDate || !toDate) return;
    setIsLoading(true);
    setError(null);

    try {
      const result = await getFrictionSignals(organizationId, siteCollectionId, {
        fromDate,
        toDate: toExclusiveToDate(toDate),
        pageId: pageId || undefined
      });
      const rawSignals = Array.isArray(result?.signals) ? result.signals : [];
      setSignals(rawSignals);
      setApiResult(result || null);
    } catch (err) {
      setError(err?.message || 'Failed to load friction signals telemetry');
      setSignals([]);
      setApiResult(null);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, siteCollectionId, fromDate, toDate, pageId]);

  useEffect(() => {
    fetchSignals();
  }, [fetchSignals]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    if (refreshNonce > 0) {
      fetchSignals();
    }
  }, [refreshNonce, fetchSignals]);

  // Open Evidence Drawer and fetch evidence
  const handleOpenEvidence = async (signal) => {
    setSelectedSignal(signal);
    setIsDrawerOpen(true);
    setEvidenceData(signal);
    setIsEvidenceLoading(true);
    setEvidenceError(null);

    try {
      const data = await getFrictionEvidence(organizationId, siteCollectionId, signal.frictionId);
      setEvidenceData(data ? { ...signal, ...data } : signal);
    } catch (err) {
      if (!signal) {
        setEvidenceError(err?.message || 'Failed to load algorithmic evidence for this signal');
      }
    } finally {
      setIsEvidenceLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedSignal(null);
    setEvidenceData(null);
  };

  // Distinct page IDs for page filter dropdown
  const distinctPageIds = Array.from(
    new Set(signals.map((s) => s.pageId).filter(Boolean))
  );

  // Filter by Page
  const pageFilteredSignals = selectedPageFilter === 'all'
    ? signals
    : signals.filter((s) => s.pageId === selectedPageFilter);

  // KPI Calculations supporting both real backend API & mock fields
  const getCountSum = (signalsList, type) =>
    signalsList
      .filter((s) => s.signalType === type)
      .reduce((sum, s) => sum + (s.count != null ? s.count : 1), 0);

  const rageClicksCount = (selectedPageFilter === 'all' && apiResult?.rageClickCount != null)
    ? apiResult.rageClickCount
    : getCountSum(pageFilteredSignals, 'rage_click');

  const deadClicksCount = (selectedPageFilter === 'all' && apiResult?.deadClickCount != null)
    ? apiResult.deadClickCount
    : getCountSum(pageFilteredSignals, 'dead_click');

  const stallsCount = (selectedPageFilter === 'all' && apiResult?.stallCount != null)
    ? apiResult.stallCount
    : getCountSum(pageFilteredSignals, 'stall');

  const repeatedValidationsCount = (selectedPageFilter === 'all' && apiResult?.repeatedValidationCount != null)
    ? apiResult.repeatedValidationCount
    : getCountSum(pageFilteredSignals, 'repeated_validation');

  const totalFrictionEvents = (selectedPageFilter === 'all' && apiResult?.totalSignals != null)
    ? apiResult.totalSignals
    : ((selectedPageFilter === 'all' && apiResult?.totalFrictionEvents != null)
        ? apiResult.totalFrictionEvents
        : (rageClicksCount + deadClicksCount + stallsCount + repeatedValidationsCount));

  // Filtered signals list
  const filteredSignals = pageFilteredSignals.filter((s) => {
    if (selectedType !== 'all' && s.signalType !== selectedType) return false;
    const severityOrConfidence = (s.severity || s.confidence || '').toLowerCase();
    if (selectedSeverity !== 'all' && severityOrConfidence !== selectedSeverity.toLowerCase()) return false;
    return true;
  });

  const getSignalBadgeClass = (type) => {
    switch (type) {
      case 'rage_click': return 'badge-rage';
      case 'dead_click': return 'badge-dead';
      case 'stall': return 'badge-stall';
      case 'repeated_validation': return 'badge-repeat';
      default: return 'badge-default';
    }
  };

  const getSignalIcon = (type) => {
    switch (type) {
      case 'rage_click': return <FaMousePointer />;
      case 'dead_click': return <FaBan />;
      case 'stall': return <FaHourglassHalf />;
      case 'repeated_validation': return <FaRedoAlt />;
      default: return <FaExclamationTriangle />;
    }
  };

  return (
    <div className="friction-signals-tab" data-testid="friction-signals-tab">
      {/* KPI Cards Row */}
      <div className="friction-kpis-grid">
        <div className="friction-kpi-card" data-testid="kpi-total-friction">
          <div className="kpi-icon-box blue">
            <FaExclamationTriangle />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Total Friction Events</span>
            <span className="kpi-num">{totalFrictionEvents.toLocaleString()}</span>
          </div>
        </div>

        <div className="friction-kpi-card" data-testid="kpi-rage-clicks">
          <div className="kpi-icon-box red">
            <FaMousePointer />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Rage Clicks</span>
            <span className="kpi-num">{rageClicksCount.toLocaleString()}</span>
          </div>
        </div>

        <div className="friction-kpi-card" data-testid="kpi-dead-clicks">
          <div className="kpi-icon-box amber">
            <FaBan />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Dead Clicks</span>
            <span className="kpi-num">{deadClicksCount.toLocaleString()}</span>
          </div>
        </div>

        <div className="friction-kpi-card" data-testid="kpi-stalls">
          <div className="kpi-icon-box purple">
            <FaHourglassHalf />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Interaction Stalls</span>
            <span className="kpi-num">{stallsCount.toLocaleString()}</span>
          </div>
        </div>

        <div className="friction-kpi-card" data-testid="kpi-repeated-validations">
          <div className="kpi-icon-box slate">
            <FaRedoAlt />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Repeated Validations</span>
            <span className="kpi-num">{repeatedValidationsCount.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="friction-error-banner" role="alert">
          <FaInfoCircle /> {error}
        </div>
      )}

      {/* Main Table Card */}
      <div className="friction-table-card">
        <div className="table-header-row">
          <div>
            <h4 className="table-card-title">Detected Behavioural Friction Signals</h4>
            <p className="table-card-sub">
              Algorithmic interaction anomalies derived from server-side heuristic analysis (APIs #18 & #19).
            </p>
          </div>

          <div className="friction-filters-group">
            {distinctPageIds.length > 0 && (
              <div className="filter-select-wrap">
                <select
                  className="friction-filter-select"
                  value={selectedPageFilter}
                  onChange={(e) => setSelectedPageFilter(e.target.value)}
                  aria-label="Filter by Page"
                  data-testid="friction-page-filter"
                >
                  <option value="all">All Pages ({signals.length})</option>
                  {distinctPageIds.map((pId) => {
                    const pObj = pages.find((p) => p.pageId === pId);
                    const pName = pObj ? (pObj.name || pId) : pId;
                    const count = signals.filter((s) => s.pageId === pId).length;
                    return (
                      <option key={pId} value={pId}>
                        {pName} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            <div className="filter-select-wrap">
              <FaFilter className="filter-icon" />
              <select
                className="friction-filter-select"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                aria-label="Filter by Signal Type"
                data-testid="friction-type-filter"
              >
                <option value="all">All Signal Types</option>
                <option value="rage_click">Rage Clicks</option>
                <option value="dead_click">Dead Clicks</option>
                <option value="stall">Interaction Stalls</option>
                <option value="repeated_validation">Repeated Validations</option>
              </select>
            </div>

            <div className="filter-select-wrap">
              <select
                className="friction-filter-select"
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                aria-label="Filter by Severity"
                data-testid="friction-severity-filter"
              >
                <option value="all">All Severities</option>
                <option value="high">High Severity</option>
                <option value="medium">Medium Severity</option>
                <option value="low">Low Severity</option>
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="friction-loading-state" data-testid="friction-loading">
            <div className="spinner" />
            <p>Loading friction telemetry...</p>
          </div>
        ) : filteredSignals.length > 0 ? (
          <div className="table-responsive">
            <table className="friction-table" data-testid="friction-signals-table">
              <thead>
                <tr>
                  <th>Signal Anomaly</th>
                  <th>Target Element</th>
                  <th>Page</th>
                  <th>Occurrences</th>
                  <th>Unique Sessions</th>
                  <th>Severity</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredSignals.map((signal) => {
                  const target = signal.elementOrField || signal.targetSelector || 'N/A';
                  const pageObj = pages.find((p) => p.pageId === signal.pageId);
                  const pageName = pageObj ? (pageObj.name || pageObj.pageId) : (signal.pageId || 'Default Page');
                  const occurrences = signal.count ?? signal.supportingEventIds?.length ?? 1;
                  const severityOrConfidence = signal.severity || signal.confidence || 'medium';

                  return (
                    <tr key={signal.frictionId} data-testid={`friction-row-${signal.frictionId}`}>
                      <td>
                        <span className={`signal-type-badge ${getSignalBadgeClass(signal.signalType)}`}>
                          <span className="badge-icon">{getSignalIcon(signal.signalType)}</span>
                          {signal.signalType?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <code className="target-code" title={target}>{target}</code>
                      </td>
                      <td className="page-cell" title={signal.pageId || ''}>
                        {pageName}
                      </td>
                      <td>
                        <strong>{occurrences.toLocaleString()}</strong>
                      </td>
                      <td>{signal.uniqueSessions?.toLocaleString() || (signal.sampleSessionIds?.length || 1)}</td>
                      <td>
                        <span className={`severity-badge ${severityOrConfidence.toLowerCase()}`}>
                          {severityOrConfidence}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="inspect-evidence-btn"
                          onClick={() => handleOpenEvidence(signal)}
                          data-testid={`inspect-evidence-btn-${signal.frictionId}`}
                        >
                          <FaSearch /> Inspect Evidence
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="friction-empty-state" data-testid="friction-empty-state">
            <FaExclamationTriangle className="empty-icon" />
            <h5>No Friction Anomalies Detected</h5>
            <p>No rage clicks, dead clicks, stalls, or repeated validation failures observed for this date range.</p>
          </div>
        )}
      </div>

      {/* Forensic Evidence Drawer */}
      <FrictionEvidenceDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        evidence={evidenceData}
        signalMeta={selectedSignal}
        isLoading={isEvidenceLoading}
        error={evidenceError}
        hasSessionReplayFeature={hasSessionReplayFeature}
        onWatchReplay={onWatchReplay}
      />
    </div>
  );
}

FrictionSignalsTab.propTypes = {
  organizationId: PropTypes.string,
  siteCollectionId: PropTypes.string,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  pageId: PropTypes.string,
  pages: PropTypes.arrayOf(PropTypes.shape({
    pageId: PropTypes.string.isRequired,
    name: PropTypes.string
  })),
  hasSessionReplayFeature: PropTypes.bool,
  onWatchReplay: PropTypes.func,
  refreshNonce: PropTypes.number,
  onLoadingChange: PropTypes.func
};
